import { ErrorHandler, Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

import { AuthService } from './auth.service';

type FrontendErrorKind = 'unhandled_error' | 'unhandled_rejection' | 'http_error';

export type FrontendErrorReport = {
  kind: FrontendErrorKind;
  message: string;
  stack?: string;
  status?: number;
  method?: string;
  request_url?: string;
  details?: Record<string, unknown>;
};

@Injectable({ providedIn: 'root' })
export class FrontendErrorReporterService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly zone = inject(NgZone);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private lastSignature = '';
  private lastSentAt = 0;

  report(report: FrontendErrorReport): void {
    if (!this.isBrowser || this.shouldSkip(report)) return;

    const user = this.auth.user();
    const payload = {
      ...report,
      url: window.location.href,
      route: this.router.url,
      // Минимум идентификации: если есть access token, backend восстановит пользователя сам.
      user_id: user?.id,
      occurred_at: new Date().toISOString(),
    };

    // Не даём логированию ошибок породить новые Angular-ошибки или циклы интерсепторов.
    this.zone.runOutsideAngular(() => {
      fetch('/api/system/frontend-errors', {
        method: 'POST',
        headers: this.buildHeaders(),
        body: JSON.stringify(payload),
        keepalive: true,
        credentials: 'include',
      }).catch(() => undefined);
    });
  }

  installGlobalBrowserListeners(errorHandler: ErrorHandler): void {
    if (!this.isBrowser) return;
    const globalState = window as Window & { __mkvmFrontendErrorListenersInstalled?: boolean };
    if (globalState.__mkvmFrontendErrorListenersInstalled) return;
    globalState.__mkvmFrontendErrorListenersInstalled = true;

    window.addEventListener('error', (event) => {
      errorHandler.handleError(event.error || event.message || 'Необработанная ошибка browser window');
    });
    window.addEventListener('unhandledrejection', (event) => {
      errorHandler.handleError({ reason: event.reason });
    });
  }

  private buildHeaders(): HeadersInit {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = this.auth.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  private shouldSkip(report: FrontendErrorReport): boolean {
    if (report.request_url?.includes('/api/system/frontend-errors')) return true;
    const signature = `${report.kind}|${report.status || ''}|${report.message}|${report.request_url || ''}`;
    const now = Date.now();
    if (signature === this.lastSignature && now - this.lastSentAt < 5000) return true;
    this.lastSignature = signature;
    this.lastSentAt = now;
    return false;
  }
}
